import {List, Record} from 'immutable';
import 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {INK_COLORS} from '~/constants/InkConstants';
import Color from './Color';
import SpotColor from './SpotColor';

function generateId(arg) {
  var initialValues = arg || {};
  initialValues.id = initialValues.id || uuid.v4();
  return initialValues;
}

export class ArtIcon extends Record({
  blackwhite: false,
  clipartVersions: {},
  categoryPath: null,
  categoryTagId: null,
  clipartId: null,
  color: INK_COLORS.Black,
  colors: List(),
  dragging: false,
  singleColor: false, // used to convert to 1 color
  height: 5,
  horizontalFlip: false,
  id: null,
  lockRatio: true,
  originalHeight: 5,
  originalWidth: 5,
  naturalHeight: null,
  naturalWidth: null,
  path: '/clipart/eps/',
  rendered: false,
  rotation: 0,
  sizeIsDirty: false,
  textIsUpdating: false,
  swappable: false,
  transparent: true,
  type: DesignIconConstants.TYPES.clipart,
  verticalFlip: false,
  viewId: 1,
  wasSaved: false,
  width: 5,
  x: 0,
  y: 0,
  recolored: false
}) {
  constructor(arg) {
    super(generateId(arg));
  }
}

export class TextIcon extends Record({
  align: 'center',
  fit: 'none',
  color: INK_COLORS.Black,
  converted: true,
  dragging: false,
  effectName: null,
  effectType: null,
  effectSetting: null,
  font: undefined,
  fontCategory: '',
  fontHeight: DesignIconConstants.TEXT.DEFAULTS.LONG_TEXT_FONT_HEIGHT,
  fontMinHeight: DesignIconConstants.TEXT.DEFAULTS.MIN_FONT_HEIGHT,
  fontName: '',
  fontOutlineAllowed: true,
  extension: '',
  height: 0,
  id: null,
  lockRatio: true,
  naturalHeight: 0,
  naturalWidth: 0,
  outlineWidth: null,
  outlineColor: null,
  rendered: false,
  rotation: 0,
  sizeIsDirty: true,
  textIsUpdating: false,
  type: DesignIconConstants.TYPES.text,
  value: '',
  verifying: false, // used to determine whether or not this icons dims are in the process of being verified
  viewId: 1,
  wasSaved: false,
  width: 0,
  x: 0,
  y: 0
}) {
  constructor(arg) {
    super({
      font: DesignIconConstants.TEXT.DEFAULTS.FONT_NAME,
      fontName: DesignIconConstants.TEXT.DEFAULTS.FONT_DISPLAY,
      ...generateId(arg)
    });
  }
}

export class UploadIcon extends Record({
  backgroundColor: null,
  contrast: null,
  crop: null,
  color: INK_COLORS.Black,
  colorMode: null,
  dragging: false,
  expirationDate: null,
  hasColorBelowThreshold: false,
  height: DesignIconConstants.UPLOAD.DEFAULTS.ICON_SIZE_INCHES,
  horizontalFlip: false,
  id: null,
  invertContrast: false,
  lockRatio: true,
  originalImageWidth: null,
  originalImageHeight: null,
  naturalHeight: null,
  naturalWidth: null,
  rendered: false,
  rotation: 0,
  singleColor: false,
  removeProductColor: false,
  sizeIsDirty: false,
  textIsUpdating: false,
  spotColors: List(),
  type: DesignIconConstants.TYPES.upload,
  url: null,
  verticalFlip: false,
  viewId: 1,
  wasSaved: false,
  width: DesignIconConstants.UPLOAD.DEFAULTS.ICON_SIZE_INCHES,
  x: 0,
  y: 0,
  isAutoSize: false
}) {
  constructor(arg) {
    super(generateId(arg));
  }
}

export class NamesIcon extends Record({
  align: 'center',
  color: INK_COLORS.Black,
  converted: true,
  dragging: false,
  font: 'Varsity01',
  fontHeight: 2,
  height: 0,
  id: null,
  rendered: false,
  rotation: 0,
  sizeIsDirty: true,
  textIsUpdating: false,
  type: DesignIconConstants.TYPES.names,
  value: 'EXAMPLE',
  verifying: false,
  viewId: 1,
  width: 0,
  x: 0,
  y: 0
}) {
  constructor(arg) {
    super(generateId(arg));
  }
}

export class NumbersIcon extends Record({
  align: 'center',
  color: INK_COLORS.Black,
  converted: true,
  dragging: false,
  font: 'Varsity01',
  fontHeight: 8,
  height: 0,
  id: null,
  rendered: false,
  rotation: 0,
  sizeIsDirty: true,
  textIsUpdating: false,
  type: DesignIconConstants.TYPES.numbers,
  value: '00',
  verifying: false,
  viewId: 1,
  width: 0,
  x: 0,
  y: 0
}) {
  constructor(arg) {
    super(generateId(arg));
  }
}

export const iconPropType = PropTypes.oneOfType([
  PropTypes.instanceOf(ArtIcon),
  PropTypes.instanceOf(UploadIcon),
  PropTypes.instanceOf(TextIcon),
  PropTypes.instanceOf(NamesIcon),
  PropTypes.instanceOf(NumbersIcon)
]);

export const namesOrNumbersPropType = PropTypes.oneOfType([
  PropTypes.instanceOf(NamesIcon),
  PropTypes.instanceOf(NumbersIcon)
]);

export const colorOrSpotColorType = PropTypes.oneOfType([
  PropTypes.instanceOf(Color),
  PropTypes.instanceOf(SpotColor)
]);
